import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function BirthdayPopup() {
  const { user } = useAuth();
  const [birthdays, setBirthdays] = useState([]);
  const [isCelebrant, setIsCelebrant] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return; // wait until user is loaded

    const today = new Date().toISOString().slice(0, 10);
    const dismissed = sessionStorage.getItem('birthday_popup_dismissed');
    if (dismissed === today) return;

    const fetchBirthdays = async () => {
      try {
        const res = await api.get('/accounts/today-birthdays/');
        const data = res.data;
        if (data.members && data.members.length > 0) {
          setBirthdays(data.members);
          setIsCelebrant(!!data.is_celebrant);
          setVisible(true);
        }
      } catch {
        // silently fail
      }
    };

    const timer = setTimeout(fetchBirthdays, 1200);
    return () => clearTimeout(timer);
  }, [user]);

  const handleClose = () => {
    setVisible(false);
    const today = new Date().toISOString().slice(0, 10);
    sessionStorage.setItem('birthday_popup_dismissed', today);
  };

  if (!visible || birthdays.length === 0) return null;

  // Other birthday members (exclude the current user if they are a celebrant)
  const otherBirthdays = isCelebrant
    ? birthdays.filter((m) => String(m.id) !== String(user?.id))
    : birthdays;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Modal — stop clicks from closing */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full overflow-y-auto"
        style={{ maxWidth: 440, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#0B5D1E] via-[#0e7526] to-[#C8A951] px-6 py-5 text-center overflow-hidden">
          <div className="absolute top-2 left-4 w-3 h-3 bg-yellow-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="absolute top-6 right-8 w-2 h-2 bg-pink-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.3s' }} />
          <div className="absolute bottom-4 left-12 w-2.5 h-2.5 bg-blue-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.6s' }} />
          <div className="absolute top-3 right-20 w-2 h-2 bg-green-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.15s' }} />
          <div className="absolute bottom-6 right-6 w-3 h-3 bg-purple-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.45s' }} />

          <div className="inline-flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-full mb-2">
            <span className="text-3xl">{isCelebrant ? '🎉' : '🎂'}</span>
          </div>
          <h2 className="text-white text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isCelebrant ? 'Happy Birthday to You! 🎂🎉' : 'Happy Birthday! 🎉'}
          </h2>
          <p className="text-white text-sm mt-1" style={{ opacity: 0.8 }}>
            {isCelebrant
              ? 'Wishing you a wonderful day!'
              : `Today we celebrate ${birthdays.length === 1 ? 'a special member' : `${birthdays.length} special members`}!`
            }
          </p>

          {/* Close X button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white bg-opacity-20 hover:bg-opacity-40 flex items-center justify-center text-white transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Celebrant's personal message from NIS-Abuja Branch */}
        {isCelebrant && (
          <div className="px-6 pt-4 pb-2">
            <div
              className="rounded-xl p-4 text-center border"
              style={{
                background: 'linear-gradient(135deg, #FFFDF5 0%, #FFF9E6 50%, #FEF3C7 100%)',
                borderColor: 'rgba(200,169,81,0.3)',
              }}
            >
              <p className="text-sm font-bold text-[#0B5D1E] mb-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                🎊 Nigerian Institution of Surveyors 🎊
              </p>
              <p className="text-xs font-semibold text-[#0B5D1E] mb-2" style={{ opacity: 0.8 }}>
                FCT (Abuja) Branch
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">
                Dear <span className="font-bold text-[#0B5D1E]">{user?.first_name || 'Esteemed Member'}</span>,
                on behalf of the <span className="font-semibold">NIS FCT (Abuja) Branch</span>,
                we wish you a very <span className="font-bold text-[#C8A951]">Happy Birthday!</span> 🎂
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-1.5">
                May this day bring you joy, good health, and continued success.
                We are proud to have you as a valued member of our great institution.
              </p>
              <p className="text-xs text-gray-500 mt-2 italic">
                Cheers to another wonderful year! 🥂🎁
              </p>
            </div>
          </div>
        )}

        {/* Other birthday members list */}
        {otherBirthdays.length > 0 && (
          <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 250 }}>
            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wider mb-3">
              {isCelebrant
                ? otherBirthdays.length === 1 ? 'Also celebrating today' : 'Also celebrating today'
                : otherBirthdays.length === 1 ? "Today's Birthday" : "Today's Birthdays"
              }
            </p>
            <div className="space-y-3">
              {otherBirthdays.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-3 p-3 rounded-xl border"
                style={{ background: 'linear-gradient(to right, #FFFDF5, #FFF9E6)', borderColor: 'rgba(200,169,81,0.2)' }}
              >
                <div className="relative flex-shrink-0">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-12 h-12 rounded-full object-cover"
                      style={{ border: '2px solid rgba(200,169,81,0.3)' }}
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: 'rgba(11,93,30,0.1)', color: '#0B5D1E' }}>
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="absolute -bottom-0.5 -right-0.5 text-base">🎈</span>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-900 truncate">{member.name}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {member.membership_number && (
                      <span className="text-xs text-gray-500 font-mono bg-white px-1.5 py-0.5 rounded border border-gray-100">
                        {member.membership_number}
                      </span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                      member.membership_status === 'Active'
                        ? 'bg-green-100 text-green-700'
                        : member.membership_status === 'Inactive'
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {member.membership_status}
                    </span>
                  </div>
                  {member.membership_category && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{member.membership_category}</p>
                  )}
                </div>

                <span className="text-2xl flex-shrink-0">🥳</span>
              </div>
            ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 pb-5 pt-2">
          <p className="text-center text-xs text-gray-400 mb-3">
            {isCelebrant
              ? 'Have a wonderful birthday celebration!'
              : 'Send them your warm wishes today!'
            }
          </p>
          <button
            onClick={handleClose}
            className="w-full text-white py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(to right, #0B5D1E, #0e7526)' }}
          >
            <span>🎊</span>
            {isCelebrant ? 'Thank You!' : 'Got it! Happy Birthday!'}
            <span>🎊</span>
          </button>
        </div>
      </div>
    </div>
  );
}
